import React from 'react'
import { useTranslation } from 'react-i18next'

import type { Account } from '../../types/accounts.types'
import { AccountsTableProps } from './AccountListTable.types'
import {
  Table,
  TableBody,
  TableProvider,
  TableRow,
  TableCell,
  TablePagination,
  Badge,
  Icon,
  Menu,
  Flex,
  TableSearchAndFilters,
  Tooltip,
} from '@percent/lemonade'

export function AccountListTable({
  accounts,
  isLoading,
  totalResults,
  nextPage,
  previousPage,
  actionsAndFilters,
  getMenuItems,
  extraColumns = [],
}: AccountsTableProps) {
  const { t } = useTranslation()

  const getDisplayValue = (value: string | null | undefined): string => {
    return value || t('table.accounts.notAvailable')
  }

  const renderRowMenu = (account: Account) => (
    <Menu
      sections={getMenuItems?.(account) ?? []}
      customTriggerComponent={
        <span
          role="button"
          tabIndex={0}
          data-testid={`account-menu-${account.id}`}
          title="Account menu"
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        >
          <Icon name="menu-vertical" size="s" />
        </span>
      }
    />
  )

  const columnsAccounts = [
    {
      id: t('table.accounts.name'),
      isSortable: false,
      props: { width: '25%' },
    },
    {
      id: t('table.accounts.email'),
      isSortable: false,
      props: { width: '30%' },
    },
    {
      id: t('table.accounts.header.status'),
      isSortable: false,
      props: { width: '5%' },
    },
    ...extraColumns.map((col) => ({
      id: col.header,
      isSortable: false,
      props: { width: col.width ?? '20%' },
    })),
    {
      id: t('table.accounts.twoFactorAuth'),
      isSortable: false,
      props: { width: '15%' },
    },
    { id: '', isSortable: false, props: { width: '5%' } },
  ]

  return (
    <TableProvider
      columns={columnsAccounts}
      totalResults={totalResults}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={t('table.accounts.emptyTable.title')}
      emptyTableDescription={t('table.accounts.emptyTable.description')}
    >
      <TableSearchAndFilters>
        <Flex direction="row" gap="md" align="center" w="100%">
          {actionsAndFilters}
        </Flex>
      </TableSearchAndFilters>
      <Table>
        <TableBody>
          {accounts?.map((account, _index) => {
            const {
              id,
              email,
              fullName,
              twoFaMandatory,
              twoFaSecretVerifiedAt,
              status,
            } = account

            return (
              <TableRow
                key={`account-list-${id}`}
                data-testid={`account-row-${id}`}
              >
                <TableCell>{getDisplayValue(fullName)}</TableCell>
                <TableCell>{email}</TableCell>
                <TableCell data-testid={`account-status-${id}`}>
                  {t(`table.accounts.status.${status}`)}
                </TableCell>
                {extraColumns.map((col) => (
                  <TableCell key={col.key}>{col.render(account)}</TableCell>
                ))}
                <TableCell inlineStyle={{ display: 'flex', gap: '8px' }}>
                  {twoFaMandatory && (
                    <Tooltip
                      content={t('table.accounts.twoFactorAuthMandatory')}
                      trigger="hover"
                    >
                      <div>
                        <Badge
                          variant="default"
                          icon="compliance"
                          testId={`account-2fa-mandatory-${id}`}
                        />
                      </div>
                    </Tooltip>
                  )}
                  <Badge
                    variant={twoFaSecretVerifiedAt ? 'positive' : 'critical'}
                    icon={twoFaSecretVerifiedAt ? 'check-badge' : 'reject'}
                    testId={`account-2fa-${
                      twoFaSecretVerifiedAt ? 'enabled' : 'disabled'
                    }-${id}`}
                  >
                    {twoFaSecretVerifiedAt
                      ? t('table.accounts.enabled')
                      : t('table.accounts.disabled')}
                  </Badge>
                </TableCell>
                <TableCell>{renderRowMenu(account)}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <TablePagination
        totalResultsText={t('table.results', { count: totalResults ?? 0 })}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </TableProvider>
  )
}
